import { Trans, useLingui } from '@lingui/react/macro'
import { useSentinelResult } from 'features/sentinels/hooks/useSentinelResult'
import { methodLabel } from 'features/sentinels/methodLabels'
import { shortTagId } from 'features/sentinels/tagFormat'
import { FC } from 'react'
import styled from 'styled-components'

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;

  th,
  td {
    text-align: left;
    padding: 0.35rem 0.5rem;
    border-bottom: 1px solid
      ${({ theme }) => theme.forecast.cardDividerBackground};
    white-space: nowrap;
  }
  th {
    color: ${({ theme }) => theme.forecast.secondaryText};
    font-weight: 600;
  }
`

const Scroll = styled.div`
  overflow-x: auto;
`

const Muted = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
`

/** One row of the compare table, loading its run's durable result by id. Each
 * saved model is its own entry (save-many), so a row per id is the natural
 * shape — and it keeps `useSentinelResult` out of a loop. */
const CompareRow: FC<{ entryId: string }> = ({ entryId }) => {
  const { i18n, t } = useLingui()

  const { record, isLoading } = useSentinelResult(entryId)

  if (isLoading || !record) {
    return (
      <tr>
        <td colSpan={5}>
          <Muted>{isLoading ? t`Loading…` : entryId}</Muted>
        </td>
      </tr>
    )
  }

  // Only agentic design runs compare here; a legacy multitag entry has no
  // top-level methods and shows dashes rather than throwing.
  const isDesignRun = record.mode !== 'multitag'
  const methods = isDesignRun ? (record.methods ?? {}) : {}
  const methodKeys = Object.keys(methods)
  const target = isDesignRun ? record.target : undefined
  const drivers = isDesignRun
    ? (record.drivers ?? record.plan?.drivers ?? [])
    : []
  const window = isDesignRun ? record.window : undefined

  return (
    <tr>
      <td>{record.name}</td>
      <td>{target ? shortTagId(target) : <Muted>—</Muted>}</td>
      <td>{drivers.length}</td>
      <td>
        {methodKeys.length
          ? methodKeys
              .map((k) => methodLabel(i18n, k, methods[k]?.label))
              .join(', ')
          : '—'}
      </td>
      <td>
        {window?.start ? (
          <Muted>
            {window.start} → {window.end}
          </Muted>
        ) : (
          <Muted>—</Muted>
        )}
      </td>
    </tr>
  )
}

export interface SentinelComparisonProps {
  entryIds: string[]
}

/**
 * Side-by-side comparison of the saved models the user ticked. Sourced from
 * each run's durable `result.json` (a `TrainResponse`), so it compares what was
 * actually fitted. Modeled on the Experiment Lab's RunComparison, but keyed on
 * saved sentinel entries rather than ephemeral session runs.
 */
export const SentinelComparison: FC<SentinelComparisonProps> = ({
  entryIds,
}) => {
  if (entryIds.length < 2) return null
  return (
    <Scroll>
      <Table>
        <thead>
          <tr>
            <th>
              <Trans>Model</Trans>
            </th>
            <th>
              <Trans>Target</Trans>
            </th>
            <th>
              <Trans>Drivers</Trans>
            </th>
            <th>
              <Trans>Methods</Trans>
            </th>
            <th>
              <Trans>Window</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {entryIds.map((entryId) => (
            <CompareRow key={entryId} entryId={entryId} />
          ))}
        </tbody>
      </Table>
    </Scroll>
  )
}
